<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12" class="pr-6">
      <card-title-row :straightTitle="additionalModuleName" class="pt-3 pb-5" />
      <sidebar-form ref="form" :is-valid.sync="isValid" is-editing-on :inputs="inputs" v-model="params" hide-rules-icon>
        <template #geometry="data">
          <data-input ref="geometry-input" v-model="params[data.name]" v-bind="data" />
          <point-summary titlePath="sidebar.visibilityAnalysis.observationPoint" :geometry="params[data.name]">
            <template #append>
              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  <v-icon
                    @click="zoomToGeometry"
                    color="rgba(0,0,0,.7)"
                    size="19px"
                    :disabled="isStickyButtonsLoading"
                    v-on="on"
                  >
                    mdi-magnify
                  </v-icon>
                </template>

                {{ $i18n.t('sidebar.visibilityAnalysis.zoomToFeatureTooltip') }}
              </v-tooltip>

              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  <v-icon
                    @click="deleteGeometry"
                    color="error"
                    size="19px"
                    :disabled="isStickyButtonsLoading"
                    v-on="on"
                  >
                    mdi-close
                  </v-icon>
                </template>

                {{ $i18n.t('sidebar.visibilityAnalysis.deleteFeatureTooltip') }}
              </v-tooltip>
            </template>
          </point-summary>
        </template>
        <template #useNmpt="data">
          <v-checkbox v-model="useNmpt">
            <template #label>
              <div class="text-body-2 text-left label-dark">
                {{ data.label }}
              </div>
            </template>
          </v-checkbox>
        </template>
        <template #saveResult>
          <dots-menu :items="dotsItems" @toShpFile="saveResult('shp')" @toDataSource="openDataSourceModal">
            <template #activator="{ on }">
              <base-button
                class="mr-0"
                v-on="on"
                :straight-text="$i18n.t('button.saveResult')"
                :loading="saveResultLoading"
                :disabled="isStickyButtonsLoading"
              />
            </template>
          </dots-menu>
        </template>
      </sidebar-form>
    </v-col>
    <main-input-dialog
      v-if="isDataSourceModalLoaded"
      :is-visible.sync="isDataSourceModalVisible"
      :loading="saveResultLoading"
      :title-translation="$i18n.t('dialog.title.saveResultToDataSource')"
      dialog-width="30%"
      @agree="saveResult('datasource')"
      :inputs="dataSourceModalInputs"
    >
      <template #[`dataSourceName.input`]="data">
        <v-col cols="6">
          <data-input v-model="resultDataSourceName" v-bind="data" />
        </v-col>
      </template>
    </main-input-dialog>
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';
import { Style, Fill, Stroke, Circle } from 'ol/style';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import PointSummary from '@/components/PointSummary';
import SidebarForm from '@/components/SidebarForm';

export default {
  name: 'VisibilityAnalysis',
  components: {
    CardTitleRow,
    DataInput,
    DotsMenu,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    PointSummary,
    SidebarForm,
  },
  computed: {
    additionalModuleName: get('admin/additionalModules.VIEWSHED.verbose_name'),
    nmtFileName: get('admin/modulesMapping@viewshed.nmt_file_name'),
    nmptFileName: get('admin/modulesMapping@viewshed.nmpt_file_name'),
    rasterFileName() {
      return this.useNmpt ? this.nmptFileName : this.nmtFileName;
    },
    dataSources: get('layers/metadata'),
    dataSourcesArray() {
      return this.$_objectToArray(this.dataSources).filter(
        dataSource => this.resultGeomTypes.includes(dataSource.geometry_type) && dataSource.scope === 'core'
      );
    },
    inputs() {
      return [
        {
          name: 'geometry',
          dataType: {
            name: 'geometry',
          },
          geometryType: 'Point',
          disableEditing: true,
          buttonProps: {
            translationPath: 'sidebar.visibilityAnalysis.drawObservationPoint',
          },
          hasNameColumn: false,
          cols: '12',
          disabled: this.params['geometry'] ? true : false,
          drawingStyleFunction: this.getPointDrawingStyleFunction(),
        },
        {
          name: 'height',
          cols: '6',
          nameCols: '4',
          dataType: { name: 'integer' },
          allowNegative: false,
          minValue: 1,
          translationPath: 'sidebar.visibilityAnalysis.observationHeight',
          rules: [v => !!v || v === 0 || this.$i18n.t('rules.required')],
        },
        {
          name: 'radius',
          cols: '6',
          nameCols: '4',
          dataType: { name: 'integer' },
          allowNegative: false,
          minValue: 1,
          translationPath: 'sidebar.visibilityAnalysis.observationRadius',
          rules: [v => !!v || v === 0 || this.$i18n.t('rules.required')],
        },
        {
          name: 'useNmpt',
          cols: '10',
          hasNameColumn: false,
          dataType: { name: 'checkbox' },
          label: this.$i18n.t('sidebar.visibilityAnalysis.useNmpt'),
        },
        ...(this.result
          ? [
              {
                name: 'saveResult',
                hasNameColumn: false,
              },
            ]
          : []),
      ];
    },
    dataSourceModalInputs() {
      return [
        {
          name: 'dataSourceName',
          translationPath: 'dialog.dataSource',
          subtitle: 'sidebar.buffer.dataSourceSubtitle',
          dataType: {
            name: 'select',
          },
          items: this.dataSourcesArray,
          itemValue: 'name',
          itemText: 'verbose_name',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },

    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
  },
  data: () => ({
    dotsItems: [{ name: 'toShpFile' }, { name: 'toDataSource' }],
    isDataSourceModalLoaded: false,
    isDataSourceModalVisible: false,
    isValid: false,
    params: {
      geometry: null,
      height: null,
      radius: null,
    },
    result: undefined,
    resultGeomTypes: [],
    resultDataSourceName: '',
    saveResultLoading: false,
    useNmpt: false,
  }),
  methods: {
    addFeatureAction: call('layers/addDatasourceFeature'),
    getVisibilityAnalysisResult: call('nmt/getVisibilityAnalysisResult'),
    exportGeojsonToShapefile: call('layers/exportGeojsonToShapefile'),
    getPointDrawingStyleFunction() {
      return () => {
        return new Style({
          image: new Circle({
            radius: 5,
            stroke: new Stroke({
              color: 'rgba(240, 10, 10, 1)',
              width: 2,
            }),
            fill: new Fill({
              color: 'rgba(240, 10, 10, 0.5)',
            }),
          }),
        });
      };
    },
    reset() {
      this.$root.$emit('toggleVisibilityAnalysisFeatures');
      this.params = {
        geometry: null,
        height: null,
        radius: null,
      };
      this.$refs.form?.$refs.form?.resetValidation();
      this.result = undefined;
      this.resultGeomTypes = [];
    },
    async generateZone() {
      this.isStickyButtonsLoading = true;
      try {
        const payload = {
          height: this.params.height,
          radius: this.params.radius,
          x: this.params.geometry.coordinates[0],
          y: this.params.geometry.coordinates[1],
          raster: this.rasterFileName,
        };
        const r = await this.getVisibilityAnalysisResult(payload);
        this.result = r.data.data;
        const resultGeomType = this.result?.features[0]?.geometry.type || '';
        if (resultGeomType === 'Polygon') {
          this.resultGeomTypes = ['polygon', 'multipolygon'];
        } else if (resultGeomType === 'MultiPolygon') {
          this.resultGeomTypes = ['multipolygon'];
        } else {
          this.resultGeomTypes = [];
        }
        this.$root.$emit('toggleVisibilityAnalysisFeatures', this.result);
        this.isStickyButtonsLoading = false;
      } finally {
        this.isStickyButtonsLoading = false;
      }
    },
    async saveResult(format, result = this.result) {
      this.saveResultLoading = true;
      try {
        await this[`saveResultTo${this.$_capitalize(format)}`](result);
      } finally {
        this.saveResultLoading = false;
      }
    },
    async saveResultToDatasource(result = this.result) {
      const dataSource = this.dataSources[this.resultDataSourceName];
      const geometry = {
        ...result.features[0].geometry,
        crs: { properties: { name: this.$_config.defaultEpsg }, type: 'name' },
      };
      const payload = {
        datasource: this.resultDataSourceName,
        body: {
          [dataSource.attributes_schema.geometry_name]: geometry,
        },
      };
      try {
        await this.addFeatureAction(payload);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('sidebar.measurementsModule.successSaveFeatures'),
        });
        this.projectLayers.forEach(layer => {
          if (layer.data_source_name === this.resultDataSourceName) {
            this.$root.$emit('refreshMvtSource', layer.id);
          }
        });
        this.isDataSourceModalVisible = false;
      } catch {
        //
      }
    },
    async saveResultToShp(result = this.result) {
      await this.exportGeojsonToShapefile(result);
    },
    openDataSourceModal() {
      this.isDataSourceModalLoaded = true;
      this.isDataSourceModalVisible = true;
    },
    deleteGeometry() {
      this.$refs[`geometry-input`]?.$refs['data-input']?.toggleOff();
    },
    zoomToGeometry() {
      this.$root.$emit('createGeomAndFitView', this.params.geometry);
    },
  },
  watch: {
    result: {
      immediate: true,
      handler(nV) {
        if (nV) {
          this.stickyButtons = [
            {
              action: 'visibilityAnalysisAction',
              translationPath: 'button.generateAgain',
            },
            {
              action: 'visibilityAnalysisReset',
              translationPath: 'button.reset',
              cancel: true,
              preventGoToLegendCancel: true,
            },
          ];
        } else {
          this.stickyButtons = [
            {
              action: 'visibilityAnalysisAction',
              translationPath: 'button.generate',
            },
          ];
        }
      },
    },
  },
  mounted() {
    this.$watch(
      vm => {
        return { isValid: vm.isValid, geometry: vm.params.geometry };
      },
      nV => {
        this.isStickyButtonsValid = nV.isValid && nV.geometry;
      },
      {
        deep: true,
        immediate: true,
      }
    );

    this.isStickyButtonsVisible = true;
    this.isStickyButtonsLoading = false;

    this.$root.$off('visibilityAnalysisAction');
    this.$root.$on('visibilityAnalysisAction', this.generateZone);
    this.$root.$off('visibilityAnalysisReset');
    this.$root.$on('visibilityAnalysisReset', this.reset);
  },
  beforeDestroy() {
    this.$root.$emit('toggleVisibilityAnalysisFeatures');
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
  },
};
</script>
<style lang="scss" scoped>
.label-dark {
  color: #202124;
}
::v-deep {
  .v-input--checkbox {
    padding-top: 0;
    margin-top: 0;
  }
}
</style>
